import axios from 'axios';

import { FEEDBACKAPI, getUserData } from '../constants/global';
import type { ICreateFeedbackRequest, IFeedbackAttachment, IFeedbackSubmission } from '../interface/feedback';

// Standalone client for the feedback service. Deliberately mirrors the admin app:
// it attaches the agent's existing core JWT but has NO 401->logout interceptor, so a
// feedback-service outage can never sign an agent out of the app.
const feedbackApi = axios.create({ baseURL: FEEDBACKAPI });

feedbackApi.interceptors.request.use((config) => {
	const token = getUserData().token;
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
});

// The feedback service sometimes returns JSON with a Content-Type that prevents
// axios from auto-parsing — coerce string bodies back to JSON.
feedbackApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

export const createFeedback = async (body: ICreateFeedbackRequest): Promise<IFeedbackSubmission> => {
	const { data } = await feedbackApi.post<IFeedbackSubmission>('/submissions', body);
	return data;
};

export const uploadFeedbackAttachment = async (file: File, type: string): Promise<IFeedbackAttachment> => {
	const formData = new FormData();
	formData.append('file', file);
	const { data } = await feedbackApi.post<IFeedbackAttachment>(`/submissions/upload?type=${type}`, formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return data;
};

export const getMyFeedback = async (): Promise<IFeedbackSubmission[]> => {
	const { data } = await feedbackApi.get<IFeedbackSubmission[]>('/submissions/mine');
	return Array.isArray(data) ? data : [];
};
